import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BillingAddonType, BillingCycle, PlanExtrasType } from 'webapp/enums/enums';
import { formatNumber, getFormattedTime } from 'webapp/utils/number';

import Serviced from 'webapp/models-custom/serviced';

// https://github.com/databox/billing-service/blob/master/src/main/java/com/databox/billing/core/type/DxCouponType.java
export enum DiscountType {
	AMOUNT = 'amount_off',
	PERCENT = 'percent_off',
}

// https://github.com/databox/billing-service/blob/master/src/main/java/com/databox/billing/enums/DxCouponIdentifier.java
export enum DiscountIdentifier {
	GRANDFATHER_DISCOUNT = 'grandfather_discount',
	EXCLUSIVE = 'exclusive',
	GROWTH_PROMO = 'growth_promo',
}

export enum DiscountDurationType {
	REPEATING = 'repeating',
}

// https://docs.stripe.com/api/subscriptions/object#subscription_object-status
export enum SubscriptionStatus {
	ACTIVE = 'active',
	CANCELED = 'canceled',
	TRIALING = 'trialing',
	PAUSED = 'paused',
	PAST_DUE = 'past_due',
	INCOMPLETE = 'incomplete',
	UNPAID = 'unpaid',
	INCOMPLETE_EXPIRED = 'incomplete_expired',
}

export type SubscriptionItemType = 'plan' | 'discount' | PlanExtrasType;

export const ITEM_ID_EXTRA_DS = 'extra_ds';

interface DiscountInfo {
	type: DiscountType;
	identifier: DiscountIdentifier;
	amount: number;
	expire: number | null;
}

export interface SubscriptionData {
	id: string;
	type: string;
	status: SubscriptionStatus;
	billingCycle: BillingCycle;
	savings?: {
		yearly?: number;
		quarterly?: number;
	};
	yearlySavings?: number;
	cancelAtPeriodEnd: boolean;
	discount: DiscountInfo | null;
	items: SubscriptionItem[];
	schedule?: SubscriptionScheduledChanges;
	planOnReactivate: string;
}

export interface SubscriptionItem {
	id: string;
	type: SubscriptionItemType;
	product_id: string;
	product_price_id: string;
	name: string;
	price: number;
	pricePerItem: number;
	quantity: number;
	formattedPrice: string;
	formattedPricePerItem: string;
	shownQuantity?: number;
	description?: string;
	priceOff?: string;
	isCustomization?: boolean;
	status: SubscriptionStatus;
}

export interface SubscriptionScheduledChanges {
	phases: {
		items: SubscriptionItem[];
	}[];
}

export default class Subscription extends Serviced {
	@service('current-user') declare currentUserService: Service.CurrentUser;

	@tracked id: string;
	@tracked type: string;
	@tracked status: SubscriptionStatus;
	@tracked billingCycle: BillingCycle;
	@tracked yearlySavings = 0;
	@tracked quarterlySavings = 0;
	@tracked cancelAtPeriodEnd = false;
	@tracked discount: DiscountInfo | null = null;
	@tracked items: SubscriptionItem[] = [];
	@tracked scheduledChanges: SubscriptionScheduledChanges['phases'] = [];
	@tracked planOnReactivate: string;

	constructor(subscriptionData: SubscriptionData) {
		super();

		this.id = subscriptionData.id;
		this.type = subscriptionData.type;
		this.status = subscriptionData.status;
		this.billingCycle = subscriptionData.billingCycle;
		this.yearlySavings =
			subscriptionData.savings?.yearly || subscriptionData.yearlySavings || 0;
		this.quarterlySavings = subscriptionData.savings?.quarterly || 0;
		this.cancelAtPeriodEnd = subscriptionData.cancelAtPeriodEnd;
		this.discount = subscriptionData.discount;
		this.items = subscriptionData.items;
		this.scheduledChanges = subscriptionData.schedule?.phases || [];
		this.planOnReactivate = subscriptionData.planOnReactivate;
	}

	// ------- status -------

	get isActive(): boolean {
		return this.status === SubscriptionStatus.ACTIVE;
	}

	get isCanceled(): boolean {
		return this.status === SubscriptionStatus.CANCELED || this.cancelAtPeriodEnd;
	}

	get isError(): boolean {
		return [
			SubscriptionStatus.PAST_DUE,
			SubscriptionStatus.INCOMPLETE,
			SubscriptionStatus.UNPAID,
			SubscriptionStatus.INCOMPLETE_EXPIRED,
		].includes(this.status);
	}

	get isTrial(): boolean {
		return this.status === SubscriptionStatus.TRIALING;
	}

	get isPaused(): boolean {
		return this.status === SubscriptionStatus.PAUSED;
	}

	get isHigherPlanTrial(): boolean {
		return this.isTrial && this.type === 'higher_plan_trial';
	}

	// ------ billingCycle -------

	get isMonthly(): boolean {
		return this.billingCycle === BillingCycle.MONTHLY;
	}

	get isQuarterly(): boolean {
		return this.billingCycle === BillingCycle.QUARTERLY;
	}

	get isYearly(): boolean {
		return this.billingCycle === BillingCycle.YEARLY;
	}

	// ------ discount info -------

	get isGrandfatherDiscount(): boolean {
		return this.discount?.identifier === DiscountIdentifier.GRANDFATHER_DISCOUNT;
	}

	get isExclusiveDiscount(): boolean {
		return this.discount?.identifier === DiscountIdentifier.EXCLUSIVE;
	}

	get isGrowthPromoDiscount(): boolean {
		return this.discount?.identifier === DiscountIdentifier.GROWTH_PROMO;
	}

	get discountType(): DiscountType | null {
		return this.discount?.type || null; // percent, absolute, or no discount
	}

	get isAmountDiscount(): boolean {
		return this.discountType === DiscountType.AMOUNT;
	}

	get isPercentualDiscount(): boolean {
		return this.discountType === DiscountType.PERCENT;
	}

	get discountAmount(): number {
		return this.discount?.amount || 0; // if type percent, then in percent, otherwise in cents
	}

	// used only for amount discounts
	get discountAmountFormatted(): string {
		return `$${formatNumber(this.discountAmount / 100, '0,0[.]00')}`;
	}

	get discountExpire(): number | null {
		return this.discount?.expire || null; // timestamp
	}

	get discountExpireFormatted(): string {
		let discountExpireFormatted = '';

		if (this.discountExpire) {
			discountExpireFormatted = getFormattedTime(
				this.discountExpire,
				this.currentUserService.model?.timezone,
			);
		}

		return discountExpireFormatted;
	}

	getDiscountedAmount(value: number): number {
		// we only need given value if the discount is percentual
		let discountedAmount;
		if (this.isPercentualDiscount) {
			discountedAmount = (this.discountAmount / 100) * value;
		} else {
			discountedAmount = this.discountAmount;
		}
		return -1 * discountedAmount;
	}

	// --------- items ----------
	// NOTE: these are current items, not upcoming state (does not take into account scheduledChanges)

	get planItem() {
		return this.items.find((item) => item.type === 'plan');
	}

	get sourcesItems() {
		return this.items.filter((item) => item.type === PlanExtrasType.EXTENSION);
	}

	get addonsItems() {
		return this.items.filter((item) => item.type === PlanExtrasType.ADD_ON);
	}

	get clientPackItem() {
		// guaranteed to have just one item (confimed by DavidK)
		return this.addonsItems.find((item) => item.id === BillingAddonType.CLIENT_PACK);
	}

	get planId(): string {
		return this.planItem?.id || '';
	}

	getCurrentAddonItemData(addonId: BillingAddonType) {
		return this.addonsItems.find((item) => item.id === addonId);
	}

	getFinalAddonItemData(addonId: BillingAddonType) {
		const itemsToSearchIn =
			this.scheduledChanges[this.scheduledChanges.length - 1]?.items || this.addonsItems;
		const addonItem = itemsToSearchIn.find((item) => item.id === addonId);
		return addonItem;
	}

	isAddonEnabled(addonId: BillingAddonType): boolean {
		const finalAddonItem = this.getFinalAddonItemData(addonId);
		return (
			!!this.getCurrentAddonItemData(addonId) &&
			!!finalAddonItem &&
			finalAddonItem.quantity > 0
		);
	}

	isAddonCanceled(addonId: BillingAddonType): boolean {
		const currentAddonItem = this.getCurrentAddonItemData(addonId);
		return (
			!!currentAddonItem &&
			(currentAddonItem.status === SubscriptionStatus.CANCELED ||
				currentAddonItem.quantity === 0)
		);
	}
}
